import type { Entity } from '@backstage/catalog-model';
import type {
  GetEntitiesByFilterCallback,
  GetEntityByRefCallback,
} from '@ipen-portal/plugin-common-react';

import { stringifyEntityRef } from '@backstage/catalog-model';
import { getDisplayName } from '@ipen-portal/plugin-common';
import { getEntityCatalogLink } from '@ipen-portal/plugin-common-react';

import type { BreadcrumbDefinition } from '../../../blueprints';
import type { BreadcrumbPartSibling, BreadcrumbPartType } from '../../../types';

const getBreadcrumbOptions = ({
  current,
  entity,
  subPath,
  suffix,
}: {
  current?: boolean;
  entity: Entity;
  subPath?: null | string;
  suffix?: null | string;
}): BreadcrumbPartSibling => {
  const link = getEntityCatalogLink(entity, subPath);
  const title = getDisplayName(entity);
  let fullTitle = title;
  if (suffix) {
    fullTitle += ` / ${suffix}`;
  }

  return {
    current,
    fullTitle,
    icon: entity?.spec?.logo as string | undefined,
    link,
    title,
  };
};

export const resolveBreadcrumbs = async (
  entity: Entity,
  currentSubPath: null | string | undefined,
  getEntitiesByFilter: GetEntitiesByFilterCallback,
  getEntityByRef: GetEntityByRefCallback,
  breadcrumbDefinitions: readonly BreadcrumbDefinition[],
) => {
  const getDefinitionForEntity = (entity: Entity) =>
    breadcrumbDefinitions.find(({ filter }) => filter(entity));

  /**
   * Resolves the parent entity
   */
  const resolveParentEntity = async (
    currentEntity: Entity,
    definition: BreadcrumbDefinition,
  ) => {
    const parentRef = definition.parent?.entityRefExtractor?.(currentEntity);
    if (!parentRef) {
      return null;
    }
    return await getEntityByRef(parentRef, {
      fallbackKind: definition.parent?.fallbackKind,
    });
  };

  const resolveSiblings = async ({
    definition,
    entity: currentEntity,
    subPath,
  }: {
    definition: BreadcrumbDefinition;
    entity: Entity;
    subPath?: null | string;
  }) => {
    const siblingsExtractor = definition.siblingsExtractor;
    if (!siblingsExtractor) {
      return [];
    }
    const request = siblingsExtractor(currentEntity);
    if (!request) {
      return [];
    }
    const entities = await getEntitiesByFilter(request);

    const ref = stringifyEntityRef(currentEntity);

    let siblings = entities
      ?.map(selectableEntity =>
        getBreadcrumbOptions({
          current: stringifyEntityRef(selectableEntity) === ref,
          entity: selectableEntity,
          subPath,
        }),
      )
      .toSorted((a, b) =>
        a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }),
      );
    if (siblings?.length === 1 && siblings?.find(sibling => sibling.current)) {
      siblings = [];
    }
    return siblings;
  };

  const resolvePartDefinitions = async (entity: Entity) => {
    const parts: BreadcrumbPartType[] = [];
    let resolvedEntity: Entity | null | undefined = entity;
    const handled = new Set<string>();
    let subPath = currentSubPath;
    let suffix: null | string | undefined = null;
    while (resolvedEntity) {
      const ref = stringifyEntityRef(resolvedEntity);
      if (handled.has(ref)) {
        break;
      }
      handled.add(ref);

      const definition = getDefinitionForEntity(resolvedEntity);
      if (!definition) {
        break;
      }

      parts.push({
        ...getBreadcrumbOptions({
          current: true,
          entity: resolvedEntity,
          subPath,
          suffix,
        }),
        entityRef: stringifyEntityRef(resolvedEntity),
        siblings: await resolveSiblings({
          definition,
          entity: resolvedEntity,
          subPath,
        }),
      });

      subPath = null;
      suffix = null;
      if (definition.root) {
        parts.push({ ...definition.root, fullTitle: definition.root.title });
        break;
      }
      if (definition.parent) {
        subPath = definition.parent.subPath;
        suffix = definition.parent.titleSuffix;
        resolvedEntity = await resolveParentEntity(resolvedEntity, definition);
      }
    }
    return parts.toReversed();
  };

  return await resolvePartDefinitions(entity);
};
